<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NButton, NDrawer, NDrawerContent, NInput } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useThreadsStore } from '../stores/threads'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { useIsMobile } from '../composables/useIsMobile'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'
import ThreadBubbleList from './ThreadBubbleList.vue'

const show = defineModel<boolean>('show', { required: true })

// Elapsed-counter tick interval for the "Thinking… Ns" indicator below.
const ELAPSED_TICK_MS = 1000

// Weak-citation nudge: the parent (RouteShell) owns the drawers,
// so the "add a source" button bubbles up rather than reaching across to
// open Add-to-wiki itself.
const emit = defineEmits<{ (e: 'add-source'): void }>()

useRestoreFocusOnClose(show)

const threads = useThreadsStore()
const wikiPages = useWikiPagesStore()
const { current, recent, submitting, submitError } = storeToRefs(threads)
const { pages } = storeToRefs(wikiPages)
const router = useRouter()
const route = useRoute()
const now = useNow()
const isMobile = useIsMobile()
// Full-screen drawer on mobile so a 390px viewport doesn't side-scroll
// behind a 560px-wide panel — same fix as AddToWikiDrawer.
const drawerWidth = computed<number | string>(() => (isMobile.value ? '100%' : 560))

const content = ref('')
const canSubmit = computed(() => content.value.trim().length > 0 && !submitting.value)
const contentInput = ref<InstanceType<typeof NInput> | null>(null)

// Sonnet calls can run 5–20s while the tool loop walks the wiki; an
// elapsed counter signals the app is alive and gives the user a rough
// "is this stuck?" yardstick without a real progress bar.
const elapsedSeconds = ref(0)
let elapsedTimer: number | undefined

watch(submitting, (running) => {
  window.clearInterval(elapsedTimer)
  if (running) {
    elapsedSeconds.value = 0
    const startedAt = Date.now()
    elapsedTimer = window.setInterval(() => {
      elapsedSeconds.value = Math.floor((Date.now() - startedAt) / ELAPSED_TICK_MS)
    }, ELAPSED_TICK_MS)
  }
})

onBeforeUnmount(() => window.clearInterval(elapsedTimer))

onMounted(() => {
  void threads.loadRecent()
  // Cold-open the drawer (e.g. from a `?thread=` bookmark) before the
  // catalog has loaded → citation chips would render raw paths. Prime
  // it so `citationLabel` resolves titles.
  if (pages.value.length === 0) void wikiPages.fetchAll().catch(() => {})
  const threadParam = route.query.thread
  if (typeof threadParam === 'string' && threadParam.length > 0) {
    const id = Number(threadParam)
    if (Number.isFinite(id) && id > 0) {
      void threads.open(id)
      show.value = true
    }
  }
})

// URL <-> thread sync. When the current thread id changes (new submit or
// explicit open), mirror it to `?thread=ID`. When the URL param changes
// externally (back-button, bookmark), load the corresponding thread.
watch(
  () => current.value?.id ?? null,
  (id) => {
    const currentParam = route.query.thread
    const next = id != null && id > 0 ? String(id) : undefined
    if (currentParam === next) return
    router.replace({ query: { ...route.query, thread: next } })
  },
)

watch(
  () => route.query.thread,
  async (value) => {
    if (typeof value !== 'string' || value.length === 0) return
    const id = Number(value)
    if (!Number.isFinite(id) || id <= 0) return
    if (current.value?.id === id) return
    await threads.open(id)
  },
)

async function submit() {
  if (!canSubmit.value) return
  const text = content.value.trim()
  content.value = ''
  try {
    await threads.submit(text)
    await threads.loadRecent()
    // Keep focus in the composer so a follow-up doesn't need a re-click.
    await nextTick()
    contentInput.value?.focus()
  } catch {
    // submitError is populated; restore the composer so the user can edit + retry.
    content.value = text
  }
}

function startNewThread() {
  threads.reset()
}

function openCitation(path: string) {
  router.push(`/p/${path}`)
  show.value = false
}

// Cmd+Enter / Ctrl+Enter submits — chat-app muscle memory. The plain
// Enter key still inserts a newline because the textarea autosizes for
// multi-paragraph follow-ups.
function onComposerEnter(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  if (!event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  void submit()
}
</script>

<template>
  <NDrawer v-model:show="show" :width="drawerWidth" placement="right">
    <NDrawerContent :native-scrollbar="false" closable>
      <template #header>
        <div class="flex items-center gap-3 min-w-0">
          <span class="truncate">
            {{ current ? current.title : 'Ask' }}
          </span>
        </div>
      </template>

      <div v-if="current" class="flex items-center gap-2 mb-3">
        <NButton size="small" quaternary @click="startNewThread">New thread</NButton>
        <span class="text-xs text-white/30">
          {{ current.messages.length }} {{ current.messages.length === 1 ? 'turn' : 'turns' }}
        </span>
      </div>

      <!-- Message bubbles: extracted to ThreadBubbleList (#1102) so
           this view stays under the 300-line hard limit. -->
      <ThreadBubbleList
        v-if="current && current.messages.length"
        :messages="current.messages"
        :submitting="submitting"
        :elapsed-seconds="elapsedSeconds"
        @open-citation="openCitation"
        @close="show = false"
        @add-source="emit('add-source')"
      />

      <form class="flex flex-col gap-2" @submit.prevent="submit">
        <NInput
          ref="contentInput"
          v-model:value="content"
          type="textarea"
          :autosize="{ minRows: 3, maxRows: 8 }"
          :placeholder="
            current ? 'Follow up… (⌘/Ctrl+Enter to send)' : 'Ask a question to start a thread… (⌘/Ctrl+Enter to send)'
          "
          :input-props="{ spellcheck: true }"
          data-testid="thread-content"
          @keydown="onComposerEnter"
        />
        <div class="flex items-center gap-2">
          <NButton
            type="primary"
            attr-type="submit"
            :loading="submitting"
            :disabled="!canSubmit"
            data-testid="thread-submit"
            >{{ current ? 'Send follow-up' : 'Start thread' }}</NButton
          >
          <p v-if="submitError" class="text-sm text-red-300 m-0">{{ submitError }}</p>
        </div>
      </form>

      <section v-if="recent.length && !current" class="mt-6 flex flex-col gap-2" data-testid="thread-recent">
        <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Recent threads</h3>
        <ul class="list-none p-0 m-0 flex flex-col gap-1">
          <li v-for="t in recent" :key="t.id" class="flex items-center gap-2">
            <NButton
              text
              type="primary"
              class="!text-sm !text-left !flex-1 !justify-start"
              data-testid="thread-recent-item"
              @click="threads.open(t.id)"
            >
              <span class="truncate">{{ t.title }}</span>
            </NButton>
            <span class="text-[11px] text-white/30 whitespace-nowrap shrink-0">
              {{ t.turn_count }} turn{{ t.turn_count === 1 ? '' : 's' }} ·
              {{ t.last_message_at ? formatDate(t.last_message_at, now) : 'new' }}
            </span>
          </li>
        </ul>
      </section>
    </NDrawerContent>
  </NDrawer>
</template>
